import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Spotlight from "@/components/Spotlight";
import UpcomingDrops from "@/components/UpcomingDrops";
import Marquee from "react-fast-marquee";
import Seo from "@/lib/seo";
import { ArrowRight, MusicNoteSimple, Waveform, ChartBar, Broadcast, ShieldCheck, CurrencyDollar } from "@phosphor-icons/react";

const HERO_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_asylum-records/artifacts/whbjrrb9_IMG_5824.webp";
const FEATURE_IMG = "https://images.unsplash.com/photo-1576967402682-19976eb930f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx1bmRlcmdyb3VuZCUyMHJhcCUyMGNvbmNlcnQlMjBzaWxob3VldHRlfGVufDB8fHx8MTc4OTQ1NjQ2N3ww&ixlib=rb-4.1.0&q=85";
const VINYL_IMG = "https://images.unsplash.com/photo-1603048588665-791ca8aea617?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHx2aW55bCUyMHJlY29yZCUyMHBsYXllciUyMHR1cm50YWJsZSUyMGRhcmt8ZW58MHx8fHwxNzg5NDU2NDgyfDA&ixlib=rb-4.1.0&q=85";

const PLATFORMS = ["SPOTIFY", "APPLE MUSIC", "TIDAL", "AMAZON MUSIC", "YOUTUBE MUSIC", "DEEZER", "PANDORA", "SOUNDCLOUD", "AUDIOMACK", "NAPSTER", "INSTAGRAM", "TIKTOK"];

const FEATURES = [
  { icon: Broadcast, title: "Global Distribution", body: "Push to 150+ streaming and social platforms in one drop. No middlemen." },
  { icon: CurrencyDollar, title: "Keep 100% Royalties", body: "You wrote it. You keep the paper. We take a flat annual fee — never a cut." },
  { icon: Waveform, title: "Studio-Grade Ingest", body: "WAV, FLAC, MP3. Automatic loudness normalization and metadata validation." },
  { icon: ChartBar, title: "Real Analytics", body: "Track streams, saves, and revenue across every DSP in one dashboard." },
  { icon: ShieldCheck, title: "Rights Protection", body: "ISRC, UPC, and content ID. Your masters, your control, always." },
  { icon: MusicNoteSimple, title: "Pre-Save + Smart Links", body: "Build hype before drop day with unified links that route by device." },
];

export default function Landing() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  useEffect(() => {
    const code = params.get("code"), state = params.get("state");
    if (!code || !state) return;
    api.post("/toolost/exchange", { code, state })
      .then(() => nav("/admin?tab=distribution&toolost=connected", { replace: true }))
      .catch((e) => nav(`/admin?tab=distribution&toolost=error&reason=${encodeURIComponent(e.response?.data?.detail || "exchange failed")}`, { replace: true }));
  }, [params, nav]);
  if (params.get("code") && params.get("state")) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-xs tracking-[0.3em] uppercase text-zinc-500" data-testid="toolost-exchanging">Connecting Too Lost…</div>;
  }
  return (
    <>
      <Seo path="/" description="Asylum Entertainment is an independent music distribution label. Upload once and release to Spotify, Apple Music, TIDAL and 150+ platforms under a real label name from $9.99/year — keep 100% of your royalties." />
      <Navbar />
      {/* PLATFORMS MARQUEE */}
      <section id="platforms" className="relative border-b border-zinc-800 py-4 marquee-fade bg-black">
        <Marquee gradient={false} speed={40}>
          {PLATFORMS.concat(PLATFORMS).map((p, i) => (
            <span key={`${p}-${i}`} className="font-display text-xl uppercase tracking-tight px-6 text-zinc-400">
              {p} <span className="text-red-600 mx-3">•</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* HERO */}
      <section className="relative border-b border-zinc-800 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Asylum Entertainment artist performing live on stage" fetchpriority="high" className="w-full h-full object-cover object-center opacity-75" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-32 lg:pb-40 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 fade-up">
            <div className="w-40 h-3 bg-red-600 mb-8" data-testid="hero-eyebrow" />
            <h1 className="font-display font-medium text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-6xl 2xl:text-7xl uppercase leading-[1.02] tracking-tight [word-spacing:0.12em] mb-8 lg:whitespace-nowrap" data-testid="hero-heading">
              Schedule your release<br />
              with Asylum...<br />
              <span className="text-red-600">You'd be <strong className="font-bold">crazy</strong> not to.</span>
            </h1>
            <p className="text-zinc-300 text-lg max-w-2xl mb-10 leading-relaxed">
              A modern record label built for artists who move without permission. Distribute to Spotify, Apple Music, TIDAL and 150+ platforms — keep every cent of your royalties.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/signup" data-testid="hero-cta-start" className="bg-red-600 text-white px-8 py-4 text-sm tracking-[0.2em] uppercase inline-flex items-center gap-3 btn-brutal btn-brutal-red">
                Start Distributing <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/pricing" data-testid="hero-cta-pricing" className="border border-zinc-700 text-white px-8 py-4 text-sm tracking-[0.2em] uppercase inline-flex items-center gap-3 btn-brutal hover:border-white transition-colors">
                See Plans
              </Link>
            </div>
          </div>
          <div className="hidden lg:flex lg:col-span-4 flex-col justify-end gap-6 border-l border-zinc-800 pl-8">
            <Stat n="150+" l="Streaming Platforms" />
            <Stat n="100%" l="Royalties Retained" />
            <Stat n="24H" l="Fastest Review" />
          </div>
        </div>
      </section>

      <Spotlight />
      <UpcomingDrops />

      {/* FEATURES */}
      <section id="how" className="border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-4">— The Platform</div>
              <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter leading-[0.95]">
                Distribution<br />done <span className="text-red-600">right.</span>
              </h2>
            </div>
            <div className="lg:col-span-8 flex items-end">
              <p className="text-zinc-400 text-lg max-w-2xl">
                Every tool a serious independent artist needs — none of the label overhead. Upload once, release everywhere, own your masters.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-800">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="border-r border-b border-zinc-800 p-8 hover:bg-zinc-950 transition-colors group" data-testid={`feature-${i}`}>
                <f.icon size={32} weight="duotone" className="text-red-600 mb-6" />
                <h3 className="font-display text-xl uppercase mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT FEATURE */}
      <section className="border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] border border-zinc-800 overflow-hidden">
            <img src={FEATURE_IMG} alt="Independent rap artist silhouetted at an underground concert" loading="lazy" className="w-full h-full object-cover grayscale contrast-125" />
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black to-transparent">
              <div className="text-[10px] tracking-[0.3em] uppercase text-red-500">— Case Study</div>
              <div className="font-display text-2xl uppercase mt-2">Independent by design.</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-4">— Why Asylum</div>
            <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter leading-[0.95] mb-8">
              Built by artists.<br />Run by artists.
            </h2>
            <div className="space-y-6 text-zinc-400">
              <p>Traditional labels take up to 80% of your streaming revenue. Asylum takes zero. You pay a flat annual fee, we handle the distribution, and every royalty dollar goes straight to your account.</p>
              <p>Whether you're dropping your first single or running a full imprint with a roster of ten, our tiered plans scale with your ambition — not your growing pains.</p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-0 border border-zinc-800">
              <Stat n="0%" l="Commission" small />
              <Stat n="1x" l="Flat Annual Fee" small />
              <Stat n="∞" l="Growth" small />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
          <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-6">— Sign the Contract</div>
          <h2 className="font-display text-5xl lg:text-7xl uppercase tracking-tighter leading-[0.9] mb-8 max-w-4xl mx-auto">
            The world is <span className="text-red-600">listening.</span><br />What are you waiting for?
          </h2>
          <Link to="/signup" data-testid="cta-bottom-signup" className="inline-flex items-center gap-3 bg-red-600 text-white px-10 py-5 text-sm tracking-[0.2em] uppercase btn-brutal btn-brutal-red">
            Claim Your Artist Account <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
      <Footer />
    </>
  );
}

function Stat({ n, l, small }) {
  return (
    <div className={`${small ? "p-6 border-r border-zinc-800 last:border-r-0" : "border-b border-zinc-800 last:border-b-0 pb-6"}`}>
      <div className={`font-display ${small ? "text-3xl" : "text-5xl"} tracking-tighter`}>{n}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-2">{l}</div>
    </div>
  );
}
